import React, { createContext, useCallback, useContext, useRef } from 'react';
import { ChatMessage } from '../data/chatTypes';
import { useAuth } from './AuthContext';
import { saveStoryChatState, type PersistedChatMsg } from '../api/storyChat';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DisplayMsg {
  id: string;
  sender: 'tutor' | 'user';
  text: string;
  timestamp: string;
  isCorrect?: boolean;
  characterImage?: any;
  characterName?: string;
  /** CCT-477 — a draw.io diagram shown in this turn's bubble, and the author's zoom. */
  diagramUrl?: string;
  diagramZoom?: number;
}

export interface AnsweredQuestion {
  id: string;
  question: ChatMessage;
  selectedAnswer: string | string[] | null;
  timestamp: string;
  /** Toutes les options essayees sur ce noeud (dont la selection finale). */
  attemptedAnswers?: string[];
}

/** Panneau de choix persistant dans le fil (missions arborescentes). */
export interface ChoiceTurnData {
  id: string;
  nodeId: string;
  options: { id: string; label: string }[];
  attemptedLabels: string[];
  pendingLabel: string | null;
  selectedLabel: string | null;
  resolved: boolean;
}

export type ChatItem =
  | { kind: 'msg'; data: DisplayMsg }
  | { kind: 'answered'; data: AnsweredQuestion }
  | { kind: 'choice-turn'; data: ChoiceTurnData };

/** Restore the labels the server grades when a linear conversation is reopened. */
export const choicesFromHistory = (items: ChatItem[]): Record<string, string> =>
  Object.fromEntries(items
    .filter((item): item is { kind: 'answered'; data: AnsweredQuestion } => item.kind === 'answered')
    .filter(item => typeof item.data.selectedAnswer === 'string')
    .map(item => [item.data.question.id, item.data.selectedAnswer as string]));

export interface ChatState {
  chatItems: ChatItem[];
  /** Position dans le script lineaire (missions quiz) */
  pos: number;
  /** Nombre de choix / reponses enregistres */
  answeredCount: number;
  isComplete: boolean;
  /** Discussion arborescente : noeud courant */
  currentNodeId?: string;
  /** Options deja essayees par noeud (session en cours). */
  attemptedChoicesByNode?: Record<string, string[]>;
}

export type ChatStateKey = string;
export const buildChatStateKey = (missionId: number, itemId?: string | number | null): ChatStateKey =>
  `${missionId}::${itemId ?? '__default__'}`;

interface ChatStateContextType {
  getState: (key: ChatStateKey) => ChatState | undefined;
  setState: (key: ChatStateKey, state: ChatState) => void;
  clearState: (key: ChatStateKey) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ChatStateContext = createContext<ChatStateContextType | undefined>(undefined);

export const useChatState = () => {
  const ctx = useContext(ChatStateContext);
  if (ctx === undefined) {
    throw new Error('useChatState must be used within a ChatStateProvider');
  }
  return ctx;
};

/** Aplatit le fil pour la persistance : ce qui a été DIT, de part et d'autre. */
export const flattenChatItems = (items: ChatItem[]): PersistedChatMsg[] => {
  const out: PersistedChatMsg[] = [];
  for (const entry of items) {
    if (entry.kind === 'msg') {
      const d = entry.data;
      out.push(d.sender === 'user'
        ? { role: 'student', text: d.text }
        : { role: 'character', text: d.text, characterName: d.characterName });
    } else if (entry.kind === 'answered') {
      // La question est DÉJÀ dans le fil comme message affiché : la réémettre ici la faisait
      // apparaître deux fois dans le centre de messagerie (CCT-97).
      const q = entry.data.question.text;
      const prev = out[out.length - 1];
      const alreadyShown = entry.data.question.exploreAll
        && out.some(m => m.role === 'character' && m.text === q);
      if (!alreadyShown && (!prev || prev.role !== 'character' || prev.text !== q))
        out.push({ role: 'character', text: q });
      const a = entry.data.selectedAnswer;
      if (a) out.push({ role: 'student', text: Array.isArray(a) ? a.join(', ') : a });
    } else if (entry.kind === 'choice-turn' && entry.data.selectedLabel) {
      out.push({ role: 'student', text: entry.data.selectedLabel });
    }
  }
  return out.filter(m => !!m.text?.trim());
};

export const ChatStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeRef = useRef<Map<ChatStateKey, ChatState>>(new Map());
  const { getAccessToken } = useAuth();
  const flushRef = useRef<Map<ChatStateKey, any>>(new Map());

  const getState = useCallback((key: ChatStateKey) => {
    return storeRef.current.get(key);
  }, []);

  const setState = useCallback((key: ChatStateKey, state: ChatState) => {
    storeRef.current.set(key, state);
    // Persistance du fil : sinon les répliques du joueur mouraient avec l'onglet et le centre
    // de messagerie rejouait un monologue (CCT-97). Débounce : un tour de chat = plusieurs
    // setState successifs, inutile d'écrire à chaque frame.
    const [mid, itemId] = key.split('::');
    const missionId = Number(mid);
    if (!Number.isFinite(missionId) || !itemId || itemId === '__default__') return;
    clearTimeout(flushRef.current.get(key));
    flushRef.current.set(key, setTimeout(() => {
      const msgs = flattenChatItems(state.chatItems ?? []);
      if (!msgs.length) return;
      void saveStoryChatState(getAccessToken(), missionId, itemId, msgs)
        .catch(() => { /* best-effort : la partie continue hors ligne */ });
    }, 800));
  }, [getAccessToken]);

  const clearState = useCallback((key: ChatStateKey) => {
    storeRef.current.delete(key);
  }, []);

  return (
    <ChatStateContext.Provider value={{ getState, setState, clearState }}>
      {children}
    </ChatStateContext.Provider>
  );
};
